import visitorAtMidnightJson from "./readings/visitor-at-midnight.json";
import theReconciliationJson from "./readings/the-reconciliation.json";
import theCostOfBeingClearJson from "./readings/the-cost-of-being-clear.json";
import anyOtherBusinessJson from "./readings/any-other-business.json";
import theLastKeyJson from "./readings/the-last-key.json";
import theWeightOfAnEmptyFrameJson from "./readings/the-weight-of-an-empty-frame.json";
import initialLessonLexiconJson from "./generated/lesson-lexicons/visitor-at-midnight.json";
import type { IntentDefinition, LessonLexicon, Reading, ReadingAppetite, ReadingAppetiteDefinition, ReadingCommitment, ReadingCommitmentDefinition, ReadingDifficultyDefinition, ReadingIntent, ReadingLexicalDifficulty } from "./types";
import { assertValidCatalog } from "./validation";
import { computeReadingMetrics } from "./metrics";

export { computeReadingMetrics } from "./metrics";

export const READING_INTENTS: IntentDefinition[] = [
  {
    id: "complex-thought",
    label: "Express complex thoughts clearly",
    description: "Distil a layered idea without flattening it.",
    outcomes: ["precise thought", "conceptual distillation"],
    rhetoricalMoves: ["contrast", "compression", "reframing"],
  },
  {
    id: "tactful-confidence",
    label: "Speak with tactful confidence",
    description: "Carry a boundary or disagreement without aggression.",
    outcomes: ["tactful firmness", "composed authority"],
    rhetoricalMoves: ["precise assertion", "refusal", "concession"],
  },
  {
    id: "difficult-vocabulary",
    label: "Build difficult vocabulary",
    description: "Meet advanced words that remain useful beyond the page.",
    outcomes: ["lexical command", "precise thought"],
    rhetoricalMoves: ["contrast", "qualification"],
  },
  {
    id: "implication",
    label: "Understand implication",
    description: "Notice what a sentence makes present without stating it.",
    outcomes: ["emotional precision", "inference"],
    rhetoricalMoves: ["implication", "understatement", "strategic silence"],
  },
  {
    id: "rhythm",
    label: "Develop rhythm",
    description: "Feel how cadence changes authority and memory.",
    outcomes: ["cadence", "memorable expression"],
    rhetoricalMoves: ["parallelism", "compression", "controlled release"],
  },
  {
    id: "force",
    label: "Use language with more force",
    description: "Make the sentence carry weight without merely raising its volume.",
    outcomes: ["composed authority", "precise thought"],
    rhetoricalMoves: ["precise assertion", "contrast", "reframing"],
  },
];

export const READING_COMMITMENTS: ReadingCommitmentDefinition[] = [
  {
    id: "brief",
    label: "A few minutes",
    description: "One complete thought or scene.",
    targetMinutes: 5,
  },
  {
    id: "sitting",
    label: "A quiet sitting",
    description: "Enough room for a fuller turn.",
    targetMinutes: 15,
  },
  {
    id: "immersion",
    label: "Let me settle in",
    description: "A chaptered work with an exact return.",
    targetMinutes: 35,
  },
];

export const READING_APPETITES: ReadingAppetiteDefinition[] = [
  { id: "tense", label: "Something tense", moods: ["tense", "mysterious", "unsettling"] },
  { id: "warm", label: "Something warm", moods: ["warm", "tender", "elegiac"] },
  { id: "wondrous", label: "Something wondrous", moods: ["wondrous", "exuberant"] },
  { id: "wry", label: "Something sharp and funny", moods: ["wry", "bracing"] },
  { id: "thoughtful", label: "Something to think with", moods: ["lucid", "bracing", "elegiac"], craftFamilies: ["argument-essay", "speculative-discipline"] },
  { id: "surprise", label: "Surprise me", moods: [] },
];

export const READING_DIFFICULTIES: ReadingDifficultyDefinition[] = [
  { id: "approachable", label: "Comfortable", description: "Rich language with a gentler lexical load." },
  { id: "advanced", label: "Stretch me", description: "A deliberate step beyond everyday vocabulary." },
  { id: "advanced-plus", label: "Demanding", description: "Dense, exact language that asks for attention." },
];

export const READINGS: Reading[] = assertValidCatalog([
  visitorAtMidnightJson as Reading,
  theReconciliationJson as Reading,
  theCostOfBeingClearJson as Reading,
  anyOtherBusinessJson as Reading,
  theLastKeyJson as Reading,
  theWeightOfAnEmptyFrameJson as Reading,
]);

export const READING_BY_ID = new Map(READINGS.map((reading) => [reading.id, reading]));
export const READING_METRICS = new Map(READINGS.map((reading) => [reading.id, computeReadingMetrics(reading)]));
export const INITIAL_LESSON_LEXICON = initialLessonLexiconJson as LessonLexicon;

const LESSON_LEXICON_LOADERS: Record<string, () => Promise<LessonLexicon>> = {
  "any-other-business": () => import("./generated/lesson-lexicons/any-other-business.json").then((module) => module.default as LessonLexicon),
  "the-cost-of-being-clear": () => import("./generated/lesson-lexicons/the-cost-of-being-clear.json").then((module) => module.default as LessonLexicon),
  "the-last-key": () => import("./generated/lesson-lexicons/the-last-key.json").then((module) => module.default as LessonLexicon),
  "the-reconciliation": () => import("./generated/lesson-lexicons/the-reconciliation.json").then((module) => module.default as LessonLexicon),
  "the-weight-of-an-empty-frame": () => import("./generated/lesson-lexicons/the-weight-of-an-empty-frame.json").then((module) => module.default as LessonLexicon),
};

export function readingCommitment(reading: Reading): ReadingCommitment {
  const minutes = computeReadingMetrics(reading).uninterruptedMinutes;
  if (minutes >= 25) return "immersion";
  if (minutes >= 8) return "sitting";
  return "brief";
}

export function loadLessonLexicon(readingId: string) {
  if (readingId === "visitor-at-midnight") return Promise.resolve(INITIAL_LESSON_LEXICON);
  return LESSON_LEXICON_LOADERS[readingId]?.() || Promise.resolve(null);
}

export function findDictionaryEntry(word: string, lexicon: LessonLexicon | null) {
  return lexicon?.entries[word.toLocaleLowerCase().replace(/[’']/g, "'").trim()];
}

export function readingTokenIds(reading: Reading) {
  const wordPattern = /^\p{L}+(?:[’'-]\p{L}+)*$/u;
  return reading.segments.flatMap((segment) =>
    segment.text
      .split(/(\p{L}+(?:[’'-]\p{L}+)*)/gu)
      .map((part, index) => (wordPattern.test(part) ? `${segment.id}-t${index}` : null))
      .filter((tokenId): tokenId is string => Boolean(tokenId)),
  );
}

export function recommendReadings(
  intentId: ReadingIntent | null,
  currentReadingId?: string,
  commitment: ReadingCommitment = "brief",
  appetiteId: ReadingAppetite = "surprise",
  difficulty: ReadingLexicalDifficulty | null = null,
) {
  const intent = READING_INTENTS.find((candidate) => candidate.id === intentId);
  const appetite = READING_APPETITES.find((candidate) => candidate.id === appetiteId);
  const targetMinutes = READING_COMMITMENTS.find((candidate) => candidate.id === commitment)?.targetMinutes || 5;
  return READINGS
    .map((reading) => {
      const outcomeMatches = intent ? reading.taxonomy.outcomes.filter((value) => intent.outcomes.includes(value)) : [];
      const moveMatches = intent ? reading.taxonomy.rhetoricalMoves.filter((value) => intent.rhetoricalMoves.includes(value)) : [];
      const matchedCraft = [...outcomeMatches, ...moveMatches].slice(0, 3);
      const moodMatches = appetite?.moods.filter((mood) => reading.taxonomy.mood.includes(mood)) || [];
      const familyMatch = appetite?.craftFamilies?.includes(reading.taxonomy.craftFamily) ? 1 : 0;
      const timeDistance = Math.abs(computeReadingMetrics(reading).uninterruptedMinutes - targetMinutes);
      const timeFit = Math.max(0, 12 - timeDistance);
      return {
        reading,
        commitmentMatch: readingCommitment(reading) === commitment,
        difficultyMatch: !difficulty || reading.taxonomy.lexicalChallenge === difficulty,
        appetiteMatch: appetiteId === "surprise" || moodMatches.length > 0 || familyMatch > 0,
        intentMatch: !intent || matchedCraft.length > 0,
        score: timeFit + outcomeMatches.length * 3 + moveMatches.length * 2 + moodMatches.length * 4 + familyMatch * 3,
        reason: matchedCraft.length || moodMatches.length
          ? `Chosen for ${[...moodMatches, ...matchedCraft].slice(0, 3).join(", ")}. ${reading.whyItFits}`
          : reading.whyItFits,
      };
    })
    .filter((candidate) => candidate.reading.id !== currentReadingId && candidate.commitmentMatch && candidate.difficultyMatch && candidate.appetiteMatch && candidate.intentMatch)
    .sort((a, b) => b.score - a.score || a.reading.title.localeCompare(b.reading.title))
    .slice(0, 4);
}

export function findWordInsight(word: string) {
  for (const reading of READINGS) {
    const entry = reading.words[word];
    if (entry) return entry;
  }
  return undefined;
}
